import { useStripe, Elements, PaymentElement, useElements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Target, AlertCircle } from "lucide-react";

// Lazy load Stripe to avoid blocking errors on other pages
let stripePromise: Promise<Stripe | null> | null = null;

function getStripePromise() {
  if (!stripePromise) {
    const key = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
    if (!key) {
      console.warn('Missing VITE_STRIPE_PUBLIC_KEY - Stripe will not be available');
      stripePromise = Promise.resolve(null);
    } else {
      stripePromise = loadStripe(key).catch((err) => {
        console.error('Failed to load Stripe:', err);
        return null;
      });
    }
  }
  return stripePromise;
}

const SubscribeForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard`,
      },
    });

    setIsProcessing(false);

    if (error) {
    } else {
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 text-primary">
          <Target className="h-8 w-8" />
        </div>
        <CardTitle>Complete Your Subscription</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <PaymentElement />
          <Button 
            type="submit" 
            className="w-full" 
            disabled={!stripe || !elements || isProcessing}
            data-testid="button-confirm-payment"
          >
            {isProcessing ? "Processing..." : "Subscribe"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default function Subscribe() {
  const [clientSecret, setClientSecret] = useState("");
  const [stripeError, setStripeError] = useState(false);
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [stripeLoading, setStripeLoading] = useState(true);

  useEffect(() => {
    // Load Stripe
    getStripePromise().then((stripe) => {
      if (!stripe) {
        setStripeError(true);
      } else {
        setStripeInstance(stripe);
      }
      setStripeLoading(false);
    });

    // Get client secret from URL params
    const params = new URLSearchParams(window.location.search);
    const secret = params.get('client_secret');
    
    if (secret) {
      setClientSecret(secret);
    } else {
      // Redirect to pricing page
      setTimeout(() => {
        window.location.href = "/pricing";
      }, 2000);
    }
  }, []);

  if (stripeLoading || !clientSecret) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading payment form...</p>
        </div>
      </div>
    );
  }

  if (stripeError || !stripeInstance) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Payment Unavailable</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Unable to load the payment system. Please try again later.
            </p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="border-b bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <Target className="h-8 w-8 text-primary" />
              <span className="text-2xl font-bold">GoalVia</span>
            </div>
            <Button variant="ghost" onClick={() => window.location.href = "/pricing"} data-testid="button-back-pricing">
              Back to Pricing
            </Button>
          </div>
        </div>
      </header>

      {/* Payment Form */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Elements stripe={stripeInstance} options={{ clientSecret }}>
            <SubscribeForm />
          </Elements>
        </div>
      </section>
    </div>
  );
}